/**
 * Expanded Edit Detection Utilities
 *
 * Determines when cell values should open in an expanded edit dialog
 * and what mode (JSON or text) should be used.
 */

import type { TableExplorerColumn } from '../../core/types/workspaceTypes';
import { looksLikeJsonString } from '../../common/utils/jsonDetection';
import type { JsonClassification } from '../../../sql-resolver/types';

export type ExpandedEditMode = 'json' | 'text';

/**
 * Subset of column metadata needed for expanded-edit detection. Callers may
 * pass a full `TableExplorerColumn`, a normalized result-grid column, or any
 * shape that exposes `dataType` + `jsonClassification`. The Result Grid path
 * populates `jsonClassification` from the JSON column lattice (JCS Phase A);
 * the Table Explorer path currently only carries `dataType`.
 */
export type ExpandedEditColumnLike = {
  dataType?: string;
  jsonClassification?: JsonClassification;
};

/**
 * JCS E2 — column carries a populated JSON classification other than `not-json`.
 * Native JSON / CHECK ISJSON / loose-JSON declared / loose-JSON inferred all
 * count as "known JSON" for the default-mode decision.
 */
function hasKnownJsonClassification(
  classification: JsonClassification | undefined,
): boolean {
  return !!classification && classification.kind !== 'not-json';
}

export type ExpandedEditDetectionResult = {
  shouldExpand: boolean;
  mode: ExpandedEditMode;
  reason: string;
};

/**
 * Minimum length for content to trigger expanded edit for text mode.
 * Short text is better edited inline.
 */
const MIN_LENGTH_FOR_EXPAND = 80;

/**
 * Maximum column size that's considered "short" and doesn't need expand option.
 * Columns like char(5), nvarchar(50), varchar(255) don't benefit from expanded editing.
 * nvarchar(256) and larger will show the expand icon.
 */
const MAX_SHORT_COLUMN_SIZE = 255;

function normalizeDataTypeForParsing(dataType: string): string {
  return dataType
    .trim()
    .toLowerCase()
    .replace(/\s+/g, ' ')
    // Handle cases like "nvarchar ( 200 )" or "decimal ( 18 , 2 )"
    .replace(/\s*\(\s*/g, '(')
    .replace(/\s*\)/g, ')')
    .replace(/\s*,\s*/g, ',');
}

/**
 * Check if the column data type is a JSON/JSONB type (PostgreSQL)
 */
export function isJsonColumnType(dataType: string | undefined): boolean {
  if (!dataType) return false;
  const normalized = normalizeDataTypeForParsing(dataType);
  return normalized === 'json' || normalized === 'jsonb';
}

/**
 * Check if the column is a text/string type
 */
export function isTextColumnType(dataType: string | undefined): boolean {
  if (!dataType) return false;
  const normalized = normalizeDataTypeForParsing(dataType);

  // JSON types
  if (normalized === 'json' || normalized === 'jsonb') return true;

  // MSSQL
  if (normalized.startsWith('varchar') || normalized.startsWith('nvarchar')) return true;
  if (normalized.startsWith('char') || normalized.startsWith('nchar')) return true;
  if (normalized === 'text' || normalized === 'ntext') return true;
  if (normalized === 'xml') return true;

  // PostgreSQL
  if (normalized.startsWith('character varying')) return true;
  if (normalized.startsWith('character')) return true;
  if (normalized === 'text' || normalized === 'citext') return true;
  if (normalized === 'bpchar' || normalized === 'name') return true;

  // Oracle
  if (normalized === 'clob' || normalized === 'nclob') return true;
  if (normalized === 'long') return true;

  return false;
}

/**
 * Check if the column is a short text type that doesn't benefit from expanded editing.
 * Examples: char(5), nvarchar(10), varchar(20)
 */
export function isShortTextColumnType(dataType: string | undefined): boolean {
  if (!dataType) return false;
  const normalized = normalizeDataTypeForParsing(dataType);

  // Fixed-length char types with small size
  const charMatch = normalized.match(/^n?char\((\d+)\)/);
  if (charMatch) {
    const size = parseInt(charMatch[1], 10);
    return !isNaN(size) && size <= MAX_SHORT_COLUMN_SIZE;
  }

  // PostgreSQL: "character(10)" is the normalized form of bpchar
  const characterMatch = normalized.match(/^character\((\d+)\)/);
  if (characterMatch) {
    const size = parseInt(characterMatch[1], 10);
    return !isNaN(size) && size <= MAX_SHORT_COLUMN_SIZE;
  }

  // Variable-length varchar types with small max size
  const varcharMatch = normalized.match(/^n?varchar\((\d+|max)\)/);
  if (varcharMatch) {
    const size = varcharMatch[1];
    if (size === 'max') return false; // max is never short
    const numSize = parseInt(size, 10);
    return !isNaN(numSize) && numSize <= MAX_SHORT_COLUMN_SIZE;
  }

  // Oracle VARCHAR2(n) / NVARCHAR2(n)
  const varchar2Match = normalized.match(/^n?varchar2\((\d+)\)/);
  if (varchar2Match) {
    const size = parseInt(varchar2Match[1], 10);
    return !isNaN(size) && size <= MAX_SHORT_COLUMN_SIZE;
  }

  // PostgreSQL: "character varying(200)" is the normalized form of varchar(n)
  const characterVaryingMatch = normalized.match(/^character varying\((\d+)\)/);
  if (characterVaryingMatch) {
    const size = parseInt(characterVaryingMatch[1], 10);
    return !isNaN(size) && size <= MAX_SHORT_COLUMN_SIZE;
  }

  // varchar without size specified - treat as potentially long
  if (normalized === 'varchar' || normalized === 'nvarchar') return false;
  if (normalized === 'character varying') return false;

  // Postgres: character defaults to length 1
  if (normalized === 'character') return true;

  // PostgreSQL: name is a fixed 63-byte type (identifiers); MSSQL: Name is a common short UDT
  if (normalized === 'name') return true;

  // text, ntext, xml, json - always potentially long
  if (['text', 'ntext', 'xml', 'json', 'jsonb', 'citext'].includes(normalized)) return false;

  return false;
}

/**
 * Check if column is a text type that could benefit from expanded editing.
 * Returns true for text columns that are NOT short.
 */
export function isExpandableTextColumnType(dataType: string | undefined): boolean {
  if (!dataType) return false;
  return isTextColumnType(dataType) && !isShortTextColumnType(dataType);
}

/**
 * Check if the column data type is a large text type that may contain
 * long text or structured data.
 */
export function isLargeTextColumnType(dataType: string | undefined): boolean {
  if (!dataType) return false;
  const normalized = normalizeDataTypeForParsing(dataType);

  // MSSQL
  if (normalized.startsWith('nvarchar(max)') || normalized.startsWith('varchar(max)')) return true;
  if (normalized === 'text' || normalized === 'ntext') return true;
  if (normalized === 'xml') return true;

  // PostgreSQL
  if (normalized === 'text') return true;
  if (normalized === 'json' || normalized === 'jsonb') return true;

  // SQLite
  if (normalized === 'text') return true;

  // Oracle
  if (normalized === 'clob' || normalized === 'nclob') return true;
  if (normalized === 'long') return true;

  // Check for large varchar/nvarchar (e.g., nvarchar(4000), varchar(8000))
  const varcharMatch = normalized.match(/^n?varchar\((\d+|max)\)/);
  if (varcharMatch) {
    const size = varcharMatch[1];
    if (size === 'max') return true;
    const numSize = parseInt(size, 10);
    if (!isNaN(numSize) && numSize >= 1000) return true;
  }

  // Oracle VARCHAR2(n) / NVARCHAR2(n) with large size
  const varchar2Match = normalized.match(/^n?varchar2\((\d+)\)/);
  if (varchar2Match) {
    const numSize = parseInt(varchar2Match[1], 10);
    if (!isNaN(numSize) && numSize >= 1000) return true;
  }

  return false;
}

/**
 * Check if a string value contains multiple lines
 */
function containsNewlines(value: string): boolean {
  return value.includes('\n');
}

/**
 * Try to parse a value as JSON object or array.
 * Returns true only for objects and arrays, not primitives.
 */
function isValidJsonObjectOrArray(value: string): boolean {
  if (!looksLikeJsonString(value)) return false;

  try {
    const parsed = JSON.parse(value);
    return typeof parsed === 'object' && parsed !== null;
  } catch {
    return false;
  }
}

/**
 * Detect the appropriate edit mode based on content and column type.
 * Priority:
 * 1. Column has a known JSON classification (JCS Phase A lattice) -> JSON mode
 * 2. JSON column data type -> JSON mode
 * 3. JSON-looking content (`{...}` / `[...]`) -> JSON mode (even if multiline/invalid)
 * 4. Content with newlines -> text mode
 * 5. Otherwise -> text mode
 */
export function detectContentMode(
  value: unknown,
  column?: ExpandedEditColumnLike
): ExpandedEditMode {
  // Null/undefined values default to text
  if (value === null || value === undefined) {
    if (column && hasKnownJsonClassification(column.jsonClassification)) {
      return 'json';
    }
    // If it's a JSON column type, prefer JSON mode
    if (column && isJsonColumnType(column.dataType)) {
      return 'json';
    }
    return 'text';
  }

  // Column carries an explicit JSON classification — wins over content shape
  // so engine-typed JSON columns open in JSON mode even when the cell value
  // doesn't itself look like JSON.
  if (column && hasKnownJsonClassification(column.jsonClassification)) {
    return 'json';
  }

  // Non-string values: if it's an object, use JSON mode
  if (typeof value !== 'string') {
    if (typeof value === 'object') {
      return 'json';
    }
    return 'text';
  }

  const strValue = value;

  // JSON column type without valid JSON content -> still JSON mode
  // (user may want to enter new JSON)
  if (column && isJsonColumnType(column.dataType)) {
    return 'json';
  }

  // JSON-looking content should open in JSON mode even when it's multiline or invalid JSON.
  if (looksLikeJsonString(strValue)) {
    return 'json';
  }

  // Content with newlines -> text mode (multiline editing)
  if (containsNewlines(strValue)) {
    return 'text';
  }

  // Default to text mode
  return 'text';
}

/**
 * Determine if a value should be edited in an expanded dialog.
 * Returns detection result with mode and reason.
 */
export function shouldExpandToDialog(
  value: unknown,
  column?: ExpandedEditColumnLike
): ExpandedEditDetectionResult {
  // Null/undefined: check column type
  if (value === null || value === undefined) {
    if (column && hasKnownJsonClassification(column.jsonClassification)) {
      return {
        shouldExpand: true,
        mode: 'json',
        reason: 'JSON column classification',
      };
    }
    if (column && isJsonColumnType(column.dataType)) {
      return {
        shouldExpand: true,
        mode: 'json',
        reason: 'JSON column type',
      };
    }
    return {
      shouldExpand: false,
      mode: 'text',
      reason: 'Empty value',
    };
  }

  // Non-string values
  if (typeof value !== 'string') {
    if (typeof value === 'object') {
      return {
        shouldExpand: true,
        mode: 'json',
        reason: 'Object value',
      };
    }
    return {
      shouldExpand: false,
      mode: 'text',
      reason: 'Simple value',
    };
  }

  const strValue = value;

  // Known JSON classification (JCS lattice) wins over content shape.
  if (column && hasKnownJsonClassification(column.jsonClassification)) {
    return {
      shouldExpand: true,
      mode: 'json',
      reason: 'JSON column classification',
    };
  }

  // Content with newlines -> expand in text mode
  if (containsNewlines(strValue)) {
    return {
      shouldExpand: true,
      mode: 'text',
      reason: 'Multiline content',
    };
  }

  // Valid JSON object/array -> expand in JSON mode
  if (isValidJsonObjectOrArray(strValue)) {
    return {
      shouldExpand: true,
      mode: 'json',
      reason: 'Valid JSON content',
    };
  }

  // JSON column type -> expand in JSON mode
  if (column && isJsonColumnType(column.dataType)) {
    return {
      shouldExpand: true,
      mode: 'json',
      reason: 'JSON column type',
    };
  }

  // Large text column type with long content -> expand in text mode
  if (column && isLargeTextColumnType(column.dataType) && strValue.length >= MIN_LENGTH_FOR_EXPAND) {
    return {
      shouldExpand: true,
      mode: 'text',
      reason: 'Long text content',
    };
  }

  // Long content even without special column type
  if (strValue.length >= MIN_LENGTH_FOR_EXPAND) {
    return {
      shouldExpand: true,
      mode: 'text',
      reason: 'Long text content',
    };
  }

  // Short simple text -> inline editing
  return {
    shouldExpand: false,
    mode: 'text',
    reason: 'Short text',
  };
}

/**
 * Determine if an expand icon should be shown for a cell.
 * Shows for expandable text columns (>255 chars) OR if content has newlines.
 * Does NOT show for short text columns (<=255) unless content has newlines.
 */
export function shouldShowExpandIcon(
  value: unknown,
  column?: TableExplorerColumn
): boolean {
  return shouldShowExpandIconForClass(classifyExpandIconColumn(column), value);
}

/**
 * Column-level expand-icon classification — the dataType-driven part of
 * `shouldShowExpandIcon`, hoisted so wide grids can classify each column ONCE
 * instead of re-parsing the dataType for every cell (measured fast-scroll
 * hotspot on 74-column tables). `shouldShowExpandIcon` above delegates to
 * these two, so the split cannot drift from the single-call behavior.
 */
export type ExpandIconColumnClass = 'always' | 'never' | 'newline-or-json' | 'content-based';

export function classifyExpandIconColumn(column?: TableExplorerColumn): ExpandIconColumnClass {
  if (column) {
    // Always show for expandable text column types (>255 chars)
    if (isExpandableTextColumnType(column.dataType)) return 'always';
    // For short text columns (<=255), only show if content has newlines or is JSON
    if (isShortTextColumnType(column.dataType)) return 'newline-or-json';
    // Don't show for non-text columns
    if (!isTextColumnType(column.dataType)) return 'never';
  }
  // Without column info (or a text type that is neither expandable nor
  // short), decide based on content.
  return 'content-based';
}

/** The value-dependent half of `shouldShowExpandIcon` — see `classifyExpandIconColumn`. */
export function shouldShowExpandIconForClass(cls: ExpandIconColumnClass, value: unknown): boolean {
  switch (cls) {
    case 'always':
      return true;
    case 'never':
      return false;
    case 'newline-or-json':
      return typeof value === 'string' && (containsNewlines(value) || isValidJsonObjectOrArray(value));
    case 'content-based': {
      if (value === null || value === undefined) return false;
      // Non-string objects should show expand icon
      if (typeof value !== 'string') return typeof value === 'object';
      // Multiline content - always show
      if (containsNewlines(value)) return true;
      // JSON content
      if (isValidJsonObjectOrArray(value)) return true;
      // Long content (when no column info available)
      return value.length >= 50;
    }
  }
}

/**
 * Check if a value is a plain object or array (not Date, RegExp, Buffer, etc.).
 * These are the object types that benefit from an expand/JSON viewer.
 */
function isExpandableObject(value: unknown): boolean {
  if (typeof value !== 'object' || value === null) return false;
  if (value instanceof Date) return false;
  if (value instanceof RegExp) return false;
  return Array.isArray(value) || Object.getPrototypeOf(value) === Object.prototype || Object.getPrototypeOf(value) === null;
}

/**
 * Determine if an expand icon should be shown in query results.
 *
 * In read mode: purely content-based (newlines, JSON, or long text ≥80 chars).
 * In edit mode: also shows for any text-type column so the user can open
 * the expanded editor to enter longer text. MySQL/PostgreSQL drivers report
 * types without size (e.g. just "varchar" not "varchar(50)"), so we can't
 * distinguish short vs long columns — but in edit mode that's acceptable.
 */
export function shouldShowExpandIconForQueryResults(
  value: unknown,
  options?: { isEditMode?: boolean; dataType?: string },
): boolean {
  if (value === null || value === undefined) {
    // In edit mode, show for expandable text columns (>255 chars) even when value is null
    if (options?.isEditMode && options?.dataType && isExpandableTextColumnType(options.dataType)) {
      return true;
    }
    return false;
  }

  // Plain objects and arrays should show expand icon (but not Date, RegExp, etc.)
  if (typeof value !== 'string') {
    return isExpandableObject(value);
  }

  const strValue = value;

  // Multiline content
  if (containsNewlines(strValue)) {
    return true;
  }

  // JSON content
  if (isValidJsonObjectOrArray(strValue)) {
    return true;
  }

  // Long content (threshold for showing icon)
  if (strValue.length >= MIN_LENGTH_FOR_EXPAND) {
    return true;
  }

  // In edit mode, show for expandable text columns (>255 chars) to allow entering longer text
  if (options?.isEditMode && options?.dataType && isExpandableTextColumnType(options.dataType)) {
    return true;
  }

  return false;
}

/**
 * Format a value for display in the expanded editor.
 * For JSON, this formats it nicely. For text, returns as-is.
 */
export function formatValueForExpandedEdit(value: unknown, mode: ExpandedEditMode): string {
  if (value === null || value === undefined) {
    return '';
  }

  if (typeof value !== 'string') {
    if (typeof value === 'object') {
      try {
        return JSON.stringify(value, null, 2);
      } catch {
        return String(value);
      }
    }
    return String(value);
  }

  if (mode === 'json') {
    // Try to format JSON nicely
    try {
      const parsed = JSON.parse(value);
      return JSON.stringify(parsed, null, 2);
    } catch {
      // Return as-is if not valid JSON
      return value;
    }
  }

  return value;
}
